/**
 * `<caos-layout-item>` — one column of a layout: how much of the row it takes, and how that changes at
 * each named breakpoint. (Catalog: Layout & containers › LayoutItem — one column of a layout, sized in twelfths of the row and resized at each named breakpoint.)
 *
 * CAOS-590. The item is the half of the pair that makes a column RESPONSIVE rather than fixed.
 *
 * ── SIZE ─────────────────────────────────────────────────────────────────────
 * Sizes are twelfths of the row. `size` holds at every width; `small-size`, `medium-size` and
 * `large-size` take over at and above that breakpoint (`src/breakpoints.ts`), each one until a larger
 * one takes over. So `size="12" medium-size="6"` is full width on a phone and half from 720px up. An
 * item with no size at all takes the width of what it holds.
 *
 * A size is a whole number from 1 to 12. Anything else — 0, 13, "half", 6.5 — matches no rule and is
 * ignored rather than guessed at, so the item falls back to the next size it does have.
 *
 * ── FLEXIBILITY ──────────────────────────────────────────────────────────────
 * How the item takes part in sharing the row: `auto` grows and shrinks to fill it, `grow` and
 * `shrink` do one of the two, `no-grow` and `no-shrink` refuse one, and `no-flex` refuses both. A size
 * is a ceiling the item grows to; `auto` and `grow` lift that ceiling, which is what lets one column
 * take whatever its neighbours leave.
 *
 * ── SPACING ──────────────────────────────────────────────────────────────────
 * `padding` insets what the item holds, from the spacing scale, on its sides or all round.
 * `alignment-bump` pushes the item away from one edge with an automatic margin: `start` pushes it to
 * the end of the row, `end` holds the items after it to the end, and `top` and `bottom` do the same
 * across the row.
 *
 * It adds no stacking layer and no overflow rule. The one thing it sets on what it holds is the width
 * available to it.
 *
 * Styling seam: `::part(item)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { BREAKPOINT_NAMES, atLeast } from '../breakpoints.js';

export const LAYOUT_ITEM_COLUMNS = 12;
export const LAYOUT_ITEM_FLEXIBILITY = ['auto', 'grow', 'shrink', 'no-grow', 'no-shrink', 'no-flex'] as const;
export const LAYOUT_ITEM_PADDING = [
  'horizontal-small',
  'horizontal-medium',
  'horizontal-large',
  'around-small',
  'around-medium',
  'around-large',
] as const;
export const LAYOUT_ITEM_ALIGNMENT_BUMP = ['start', 'end', 'top', 'bottom'] as const;

/** The attribute that carries each breakpoint's size: `medium` → `medium-size`. */
export const sizeAttribute = (name: (typeof BREAKPOINT_NAMES)[number]): string => `${name}-size`;

/** One rule per whole twelfth, for one size attribute. */
function widths(attribute: string): string {
  const rules: string[] = [];
  for (let n = 1; n <= LAYOUT_ITEM_COLUMNS; n++) {
    const share = `${((n * 100) / LAYOUT_ITEM_COLUMNS).toFixed(4)}%`;
    rules.push(`:host([${attribute}="${n}"]) { flex-basis: ${share}; max-width: ${share}; }`);
  }
  return rules.join('\n  ');
}

const SPACE = { small: 'var(--caos-space-2)', medium: 'var(--caos-space-3)', large: 'var(--caos-space-4)' } as const;

const sheet = makeSheet(`
  :host {
    display: block;
    box-sizing: border-box;
    flex: 0 1 auto;
    min-width: 0;
  }
  .item { min-width: 0; }

  ${widths('size')}
  ${BREAKPOINT_NAMES.map((name) => `@media ${atLeast(name)} {\n  ${widths(sizeAttribute(name))}\n  }`).join('\n  ')}

  /* After every size rule, so a flexibility that lifts the ceiling wins at every width. */
  :host([flexibility="auto"]) { flex-grow: 1; flex-shrink: 1; max-width: none; }
  :host([flexibility="grow"]) { flex-grow: 1; max-width: none; }
  :host([flexibility="shrink"]) { flex-shrink: 1; }
  :host([flexibility="no-grow"]) { flex-grow: 0; }
  :host([flexibility="no-shrink"]) { flex-shrink: 0; }
  :host([flexibility="no-flex"]) { flex-grow: 0; flex-shrink: 0; }

  ${Object.entries(SPACE).map(([step, value]) => `:host([padding="horizontal-${step}"]) { padding-inline: ${value}; }\n  :host([padding="around-${step}"]) { padding: ${value}; }`).join('\n  ')}

  :host([alignment-bump="start"]) { margin-inline-start: auto; }
  :host([alignment-bump="end"]) { margin-inline-end: auto; }
  :host([alignment-bump="top"]) { margin-top: auto; }
  :host([alignment-bump="bottom"]) { margin-bottom: auto; }
`);

export class CaosLayoutItem extends CaosElement {
  constructor() {
    super(sheet);
    const item = document.createElement('div');
    item.className = 'item';
    item.setAttribute('part', 'item');
    item.append(document.createElement('slot'));
    this.root.append(item);
  }
}

define('caos-layout-item', CaosLayoutItem);

declare global {
  interface HTMLElementTagNameMap {
    'caos-layout-item': CaosLayoutItem;
  }
}
